import IndexCard from 'components/common/IndexCard';
import CircleLoader from 'components/common/CircleLoader';
import ErrorDisplay from 'components/common/ErrorDisplay';
import { DisplayEventLogEntry } from 'services/backend/eventLog';
import { useSurveyEventLog } from 'hooks/backend/eventLog';
import { isAxiosError } from 'axios';

const EVENT_LOG_GRID_COLUMNS = '180px 150px 200px 120px 100px 120px';

interface AdminEventLogProps {
  surveyId: number;
}

const AdminEventLog = ({ surveyId }: AdminEventLogProps) => {
  const {
    data: allLogs = [],
    isLoading,
    isError,
    error,
  } = useSurveyEventLog({ surveyId });

  if (isLoading) {
    return (
      <div className="mt-6">
        <IndexCard>
          <div className="flex flex-col justify-center items-center py-12">
            <CircleLoader isColored />
            <div className="mt-3 text-sm text-gray-600">
              Loading event log...
            </div>
          </div>
        </IndexCard>
      </div>
    );
  }

  if (isError) {
    // Provide more specific error messages based on error type
    let errorMessage = 'Failed to load event log. Please try again.';

    if (isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 404) {
        errorMessage =
          "Survey not found or you don't have permission to view its event log.";
      } else if (status === 403) {
        errorMessage =
          "You don't have permission to view this survey's event log.";
      } else if (status && status >= 500) {
        errorMessage =
          'Server error occurred while loading event log. Please try again later.';
      } else if (!navigator.onLine) {
        errorMessage =
          'No internet connection. Please check your connection and try again.';
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }

    return (
      <div className="mt-6">
        <IndexCard>
          <div className="p-4">
            <ErrorDisplay message={errorMessage}>
              <span className="block mt-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                  onClick={() => window.location.reload()}
                >
                  Refresh page
                </button>
              </span>
            </ErrorDisplay>
          </div>
        </IndexCard>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-6">
        <IndexCard>
          <div>
            <div
              className="grid gap-4 justify-items-center py-2 px-1 border-b border-gray-300 text-gray-600 font-normal bg-white sticky top-0 z-10"
              style={{
                gridTemplateColumns: EVENT_LOG_GRID_COLUMNS,
              }}
            >
              <div className="justify-self-start" role="columnheader">
                Date
              </div>
              <div className="justify-self-start" role="columnheader">
                Status
              </div>
              <div className="justify-self-start" role="columnheader">
                Who
              </div>
              <div className="justify-self-start" role="columnheader">
                Number of respondents
              </div>
              <div className="justify-self-start" role="columnheader">
                IR
              </div>
              <div className="justify-self-start" role="columnheader">
                Number of questions
              </div>
            </div>
            <div className="space-y-2">
              <EventLogRows logs={allLogs} />
            </div>
          </div>
        </IndexCard>
      </div>
    </div>
  );
};

function NoEventsRow() {
  return (
    <div
      className="grid items-center gap-4 py-8 px-1"
      style={{ gridTemplateColumns: EVENT_LOG_GRID_COLUMNS }}
    >
      <div className="col-span-6 text-center text-gray-500">
        <div className="text-sm">No events found.</div>
        <div className="text-xs mt-1">
          Event log entries will appear here when survey status changes occur.
        </div>
      </div>
    </div>
  );
}

function EventLogRows({ logs }: { logs: DisplayEventLogEntry[] }) {
  if (!logs || !logs.length) {
    return <NoEventsRow />;
  }

  return (
    <>
      {logs.map((event) => (
        <div
          key={event.id}
          className="grid items-center justify-items-center gap-4 py-2 px-1 border-b border-gray-300"
          style={{ gridTemplateColumns: EVENT_LOG_GRID_COLUMNS }}
        >
          <div className="justify-self-start text-sm text-gray-900 truncate">
            {new Date(event.createdAt).toLocaleString()}
          </div>
          <div className="justify-self-start text-sm text-gray-900 truncate">
            {event.statusName}
          </div>
          <div className="justify-self-start text-sm text-gray-900">
            {event.isUserSystem ? (
              'System'
            ) : (
              <div>
                <div>{event.userFullName}</div>
                <div className="text-xs text-gray-600">{event.userEmail}</div>
              </div>
            )}
          </div>
          <div className="justify-self-end text-sm text-gray-900">
            {event.respondentCount}
          </div>
          <div className="justify-self-center text-sm text-gray-900">
            {event.incidenceRate}
          </div>
          <div className="justify-self-end text-sm text-gray-900">
            {event.questionCount}
          </div>
        </div>
      ))}
    </>
  );
}

export default AdminEventLog;
